import styles from 'css/src/components/app/aspect-ratio-picker.module.scss';

import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import type { AspectRatio } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';
import { getSupportedAspectRatios } from 'js/src/data/templates/templates-selectors';
import type { RootState } from 'js/src/libs/RootState';

type AspectRatioOptionProps = {
    aspectRatio: AspectRatio;
    label: string;
    isSelected: boolean;
    isDisabled: boolean;
    onSelect: (aspectRatio: AspectRatio) => void;
    tooltipText?: string;
};

const AspectRatioOption = React.memo(function AspectRatioOption({
    aspectRatio,
    label,
    isSelected,
    isDisabled,
    onSelect,
    tooltipText = '',
}: AspectRatioOptionProps) {
    const className = classNames(styles['format-picker-wrapper'], {
        [styles['active']]: isSelected,
        [styles['disabled']]: isDisabled,
    });

    const onClick = useCallback(() => {
        if (!isDisabled) {
            onSelect(aspectRatio);
        }
    }, [onSelect, aspectRatio, isDisabled]);

    const aspectRatioClass: Partial<Record<AspectRatio, string>> = {
        landscape: styles['landscape'],
        vertical: styles['vertical'],
        fourfive: styles['fourfive'],
    };

    let optionNode = (
        <div className={className} key={aspectRatio} onClick={onClick}>
            <div className={classNames(styles['outer'], aspectRatioClass[aspectRatio])}>
                <span>{label}</span>
            </div>
        </div>
    );

    if (tooltipText) {
        optionNode = (
            <Tooltip title={tooltipText} placement={Placement.TOP}>
                {optionNode}
            </Tooltip>
        );
    }

    return optionNode;
});

type AspectRatioPickerProps = {
    templateId: string | null;
    selectedAspectRatio: AspectRatio;
    onAspectRatioChange: (aspectRatio: AspectRatio) => void;
    containerClassName?: string;
    disabledAspectRatios?: ReadonlySet<AspectRatio>;
    // A single tooltip for every disabled option, or a function when the
    // reason differs per aspect ratio.
    disabledTooltip?: string | ((aspectRatio: AspectRatio) => string | undefined);
};

const AspectRatioPicker = React.memo(function AspectRatioPicker({
    templateId,
    selectedAspectRatio,
    onAspectRatioChange,
    containerClassName,
    disabledAspectRatios,
    disabledTooltip,
}: AspectRatioPickerProps) {
    const supportedAspectRatios = useSelector((state: RootState) =>
        getSupportedAspectRatios(state, templateId),
    );

    useEffect(() => {
        if (!supportedAspectRatios || supportedAspectRatios.length === 0) return;

        const isUnsupported = !supportedAspectRatios.includes(selectedAspectRatio);
        const isDisabled = disabledAspectRatios?.has(selectedAspectRatio);

        if (isUnsupported || isDisabled) {
            const firstEnabled = supportedAspectRatios.find((ar) => !disabledAspectRatios?.has(ar));
            if (firstEnabled) {
                onAspectRatioChange(firstEnabled);
            }
        }
    }, [supportedAspectRatios, selectedAspectRatio, onAspectRatioChange, disabledAspectRatios]);

    return (
        <div className={classNames('d-flex align-items-center gap-3', containerClassName)}>
            {supportedAspectRatios?.map((aspectRatio) => {
                const isDisabled = disabledAspectRatios?.has(aspectRatio) ?? false;
                const tooltipText =
                    typeof disabledTooltip === 'function'
                        ? disabledTooltip(aspectRatio)
                        : disabledTooltip;
                return (
                    <AspectRatioOption
                        key={aspectRatio}
                        aspectRatio={aspectRatio}
                        label={VideoData.getAspectRatioDisplayRatio(aspectRatio)}
                        isSelected={selectedAspectRatio === aspectRatio}
                        isDisabled={isDisabled}
                        onSelect={onAspectRatioChange}
                        tooltipText={isDisabled ? tooltipText : undefined}
                    />
                );
            })}
        </div>
    );
});

export default AspectRatioPicker;
